'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bot,
  Check,
  ChevronRight,
  Mic,
  PhoneCall,
  Plus,
  Send,
  ShoppingBag,
  Sparkles,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import type {
  AssistantItemCard,
  RestaurantAssistantAction,
} from '@/lib/restaurant-assistant-contract';

interface MenuItem {
  id: string;
  name: string;
  price: number;
  description?: string;
  image?: string | null;
}

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface CustomerInfo {
  name: string;
  phone: string;
  email: string;
  notes: string;
}

interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
  cards?: AssistantItemCard[];
  quickReplies?: string[];
}

export type { RestaurantAssistantAction } from '@/lib/restaurant-assistant-contract';

interface RestaurantAssistantProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantName: string;
  tenantPhone: string;
  branch?: {
    name?: string | null;
    phone?: string | null;
    address?: string | null;
    hours?: string | null;
  } | null;
  apiQuery: string;
  items: MenuItem[];
  cart: CartItem[];
  customer: CustomerInfo;
  pageContext: {
    activeTab: string;
    searchQuery: string;
    checkoutOpen: boolean;
  };
  onAction: (action: RestaurantAssistantAction) => void;
}

type SpeechRecognitionResults = {
  length: number;
  [index: number]: {
    isFinal?: boolean;
    [index: number]: { transcript?: string };
  };
};

type SpeechRecognitionConstructor = new () => {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: { results: SpeechRecognitionResults }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
};

const QUICK_PROMPTS = [
  'Sugere um jantar para duas pessoas',
  'Quais são os pratos mais completos?',
  'Ajuda-me a finalizar a encomenda',
];

const FALLBACK_DISH_IMAGE = 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=720&q=80';
const ASSISTANT_IMAGE_RULES = [
  { keys: ['frango', 'churrasco', 'grelhado', 'carne', 'menu'], src: 'https://images.unsplash.com/photo-1532550907401-a500c9a57435?auto=format&fit=crop&w=720&q=80' },
  { keys: ['peixe', 'bacalhau', 'dourada', 'robalo', 'salmao'], src: 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?auto=format&fit=crop&w=720&q=80' },
  { keys: ['arroz', 'batata', 'salada', 'legumes', 'acompanhamento'], src: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=720&q=80' },
  { keys: ['sobremesa', 'pudim', 'mousse', 'doce'], src: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?auto=format&fit=crop&w=720&q=80' },
];

function speechCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
}

function speak(text: string) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'pt-PT';
  utterance.rate = 1.02;
  window.speechSynthesis.speak(utterance);
}

function formatPrice(value: number) {
  return `${Number(value).toFixed(2)} EUR`;
}

function resolveCardImage(card: AssistantItemCard, localImage?: string | null) {
  if (card.image || localImage) return card.image || localImage || FALLBACK_DISH_IMAGE;
  const haystack = `${card.name} ${card.category}`.toLocaleLowerCase('pt-PT');
  return ASSISTANT_IMAGE_RULES.find((rule) => rule.keys.some((key) => haystack.includes(key)))?.src || FALLBACK_DISH_IMAGE;
}

export function RestaurantAssistant({
  open,
  onOpenChange,
  tenantName,
  tenantPhone,
  branch,
  apiQuery,
  items,
  cart,
  customer,
  pageContext,
  onAction,
}: RestaurantAssistantProps) {
  const [messages, setMessages] = useState<AssistantMessage[]>([
    {
      role: 'assistant',
      content: `Olá! Sou o guia da ${tenantName}. Dizes-me para quantas pessoas é o pedido e eu trato do resto contigo.`,
      quickReplies: QUICK_PROMPTS,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [listening, setListening] = useState(false);
  const [speechOutput, setSpeechOutput] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<InstanceType<SpeechRecognitionConstructor> | null>(null);

  const supportsSpeech = useMemo(() => Boolean(speechCtor()), []);
  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.quantity, 0), [cart]);
  const knownDetails = [customer.name, customer.phone].filter(Boolean).length;

  useEffect(() => {
    if (!open) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 180);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [onOpenChange, open]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      setInput('');
      setError('');
      const nextMessages: AssistantMessage[] = [...messages, { role: 'user', content: trimmed }];
      setMessages(nextMessages);
      setLoading(true);

      try {
        const res = await fetch(`/api/public/assistant${apiQuery}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            input: trimmed,
            messages: nextMessages.slice(-10).map(({ role, content }) => ({ role, content })),
            cart,
            customer,
            selected_branch: branch ?? null,
            page_context: {
              ...pageContext,
              cart_count: cartCount,
              cart_total: Number(cartTotal.toFixed(2)),
              customer_details_complete: Boolean(customer.name && customer.phone),
            },
          }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Não foi possível falar com o assistente.');

        const reply = data.reply || 'Estou aqui para ajudar com a encomenda.';
        setMessages((current) => [...current, {
          role: 'assistant',
          content: reply,
          cards: Array.isArray(data.cards) ? data.cards : [],
          quickReplies: Array.isArray(data.quick_replies) ? data.quick_replies : [],
        }]);
        if (Array.isArray(data.actions)) {
          data.actions.forEach((action: RestaurantAssistantAction) => onAction(action));
        }
        if (speechOutput) speak(reply);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro ao contactar o assistente.';
        setError(message);
        setMessages((current) => [
          ...current,
          {
            role: 'assistant',
            content: 'Não consegui tratar disso agora. O teu carrinho está guardado; tenta novamente ou liga para o restaurante.',
            quickReplies: ['Tentar novamente'],
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [apiQuery, branch, cart, cartCount, cartTotal, customer, loading, messages, onAction, pageContext, speechOutput],
  );

  const toggleListening = useCallback(() => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }

    const Constructor = speechCtor();
    if (!Constructor) {
      setError('O reconhecimento de voz não está disponível neste browser. Podes continuar por texto.');
      return;
    }

    window.speechSynthesis?.cancel();
    const recognition = new Constructor();
    let transcript = '';
    recognition.lang = 'pt-PT';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      transcript = Array.from({ length: event.results.length }, (_, index) => event.results[index]?.[0]?.transcript || '')
        .join(' ')
        .trim();
      setInput(transcript);
    };
    recognition.onerror = () => {
      setListening(false);
      setError('Não consegui ouvir com clareza. Tenta novamente ou escreve o pedido.');
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
      if (transcript) void sendMessage(transcript);
    };
    recognitionRef.current = recognition;
    setError('');
    setListening(true);
    recognition.start();
  }, [listening, sendMessage]);

  useEffect(() => {
    if (open) return;
    recognitionRef.current?.stop();
    setListening(false);
    window.speechSynthesis?.cancel();
  }, [open]);

  if (!open) return null;

  return (
    <div className="assistant-shell" role="dialog" aria-modal="true" aria-label="Assistente de encomendas">
      <div className="assistant-backdrop" onClick={() => onOpenChange(false)} />
      <section className="assistant-panel">
        <header className="assistant-header">
          <div className="assistant-avatar" aria-hidden="true">
            <Bot className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="assistant-title">Guia {tenantName}</h2>
            <p className="assistant-subtitle">
              {listening ? 'A ouvir… fala naturalmente' : `${branch?.name || 'Loja atual'} · contexto atualizado`}
            </p>
          </div>
          <button
            type="button"
            className={`assistant-icon-button ${speechOutput ? 'is-active' : ''}`}
            onClick={() => {
              setSpeechOutput((value) => !value);
              window.speechSynthesis?.cancel();
            }}
            aria-label={speechOutput ? 'Desligar voz do assistente' : 'Ligar voz do assistente'}
            title="Resposta falada"
          >
            {speechOutput ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          <button
            type="button"
            className="assistant-icon-button"
            onClick={() => onOpenChange(false)}
            aria-label="Fechar assistente"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="assistant-context" aria-label="Contexto conhecido pelo assistente">
          <span><ShoppingBag className="h-3.5 w-3.5" /> {cartCount ? `${cartCount} no carrinho` : 'Carrinho vazio'}</span>
          <span><Check className="h-3.5 w-3.5" /> {knownDetails === 2 ? 'Dados prontos' : `${knownDetails}/2 dados`}</span>
        </div>

        <div className="assistant-messages" aria-live="polite">
          {messages.map((message, index) => (
            <div key={`${message.role}-${index}`} className={`assistant-message-group ${message.role}`}>
              <div className="assistant-message-row">
                <div className="assistant-message">{message.content}</div>
              </div>

              {message.role === 'assistant' && message.cards && message.cards.length > 0 && (
                <div className="assistant-cards" aria-label="Sugestões da ementa">
                  {message.cards.map((card) => {
                    const quantity = cart
                      .filter((item) => item.id === card.id)
                      .reduce((sum, item) => sum + item.quantity, 0);
                    const localItem = items.find((item) => item.id === card.id);
                    return (
                      <article className="assistant-item-card" key={card.id}>
                        <img
                          src={resolveCardImage(card, localItem?.image)}
                          alt={card.name}
                          loading="lazy"
                          onError={(event) => { event.currentTarget.src = FALLBACK_DISH_IMAGE; }}
                        />
                        <div className="assistant-item-card-body">
                          <span className="assistant-item-category">{card.category}</span>
                          <h3>{card.name}</h3>
                          {card.description && <p>{card.description}</p>}
                          <div className="assistant-item-card-footer">
                            <strong>{formatPrice(card.price)}</strong>
                            <button
                              type="button"
                              onClick={() => onAction({ type: 'add_item', item_id: card.id, quantity: 1 })}
                              aria-label={`Adicionar ${card.name} ao carrinho`}
                            >
                              {quantity > 0 ? <span>{quantity}</span> : <Plus className="h-4 w-4" />}
                              {quantity > 0 ? 'Adicionar mais' : 'Adicionar'}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {message.role === 'assistant' && index === messages.length - 1 && message.quickReplies && message.quickReplies.length > 0 && (
                <div className="assistant-quick-replies" aria-label="Respostas sugeridas">
                  {message.quickReplies.map((reply) => (
                    <button type="button" key={reply} onClick={() => void sendMessage(reply)} disabled={loading}>
                      {reply}<ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="assistant-message-group assistant">
              <div className="assistant-message-row">
                <div className="assistant-typing" aria-label="O assistente está a preparar a resposta">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <footer className="assistant-composer">
          {cartCount > 0 && (
            <div className="assistant-cart-summary">
              <button type="button" onClick={() => onAction({ type: 'open_cart' })}>
                <ShoppingBag className="h-4 w-4" />
                <span><strong>{cartCount} {cartCount === 1 ? 'item' : 'itens'}</strong><small>{formatPrice(cartTotal)}</small></span>
                Rever
              </button>
              <button type="button" onClick={() => onAction({ type: 'open_checkout' })}>Finalizar</button>
            </div>
          )}
          {error && <p className="assistant-error" role="alert">{error}</p>}
          <div className="assistant-input-row">
            <button
              type="button"
              className={`assistant-mic-button ${listening ? 'is-listening' : ''}`}
              onClick={toggleListening}
              disabled={!supportsSpeech || loading}
              aria-label={listening ? 'Parar gravação' : 'Ditar pedido'}
              title={supportsSpeech ? 'Ditar pedido' : 'Voz indisponível neste browser'}
            >
              {listening ? <span className="assistant-listening-dot" /> : <Mic className="h-4 w-4" />}
            </button>
            <input
              ref={inputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void sendMessage(input);
                }
              }}
              placeholder={listening ? 'Estou a ouvir…' : 'Ex.: jantar para 3, sem picante'}
              disabled={loading}
            />
            <button type="button" className="assistant-send-button" onClick={() => void sendMessage(input)} disabled={!input.trim() || loading} aria-label="Enviar">
              <Send className="h-4 w-4" />
            </button>
          </div>
          <div className="assistant-footer-links">
            <span><Sparkles className="h-3.5 w-3.5" /> Ementa, carrinho e dados sincronizados</span>
            <a href={`tel:${tenantPhone}`}><PhoneCall className="h-3.5 w-3.5" /> Ligar</a>
          </div>
        </footer>
      </section>
    </div>
  );
}
